import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import PageNav from "../components/site/PageNav";
import Footer from "../components/site/Footer";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

const mdComponents = {
  h1: (p) => <h2 className="font-display text-3xl font-bold text-white mt-12 mb-4" {...p} />,
  h2: (p) => <h2 className="font-display text-2xl font-bold text-white mt-12 mb-4" {...p} />,
  h3: (p) => <h3 className="font-display text-xl font-bold text-white mt-8 mb-3" {...p} />,
  p: (p) => <p className="font-body text-white/70 leading-relaxed text-lg mb-6" {...p} />,
  a: (p) => <a className="text-[#00D166] underline underline-offset-4 hover:text-white transition-colors" target="_blank" rel="noopener noreferrer" {...p} />,
  ul: (p) => <ul className="list-disc pl-6 space-y-2 mb-6 font-body text-white/70 text-lg" {...p} />,
  ol: (p) => <ol className="list-decimal pl-6 space-y-2 mb-6 font-body text-white/70 text-lg" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  blockquote: (p) => <blockquote className="border-l-2 border-[#00D166] pl-6 my-8 font-serifed italic text-2xl text-white/80" {...p} />,
  code: (p) => <code className="bg-[#0F172A] text-[#00D166] px-1.5 py-0.5 text-sm font-mono" {...p} />,
  img: (p) => <img className="w-full border border-white/10 my-8" alt={p.alt || ""} {...p} />,
  hr: () => <hr className="border-white/10 my-10" />,
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="bg-[#030712] min-h-screen" data-testid="blogpost-notfound">
        <PageNav />
        <div className="mx-auto max-w-[900px] px-6 py-32 text-center">
          <h1 className="font-display text-4xl font-bold text-white">Post not found</h1>
          <button onClick={() => navigate("/blog")} className="mt-6 font-body text-sm text-[#00D166] hover:text-white transition-colors">← Back to Insights</button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="bg-[#030712] min-h-screen">
        <PageNav />
        <div className="mx-auto max-w-[900px] px-6 py-32"><p className="font-body text-white/50">Loading…</p></div>
      </div>
    );
  }

  return (
    <div className="bg-[#030712] min-h-screen" data-testid="blogpost-page">
      <Helmet>
        <title>{`${post.title} — Yes Global Consultants`}</title>
        <meta name="description" content={post.excerpt || post.title} />
        <link rel="canonical" href={`https://yesconsultants.in/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${post.title} — Yes Global Consultants`} />
        <meta property="og:description" content={post.excerpt || post.title} />
        <meta property="og:url" content={`https://yesconsultants.in/blog/${post.slug}`} />
        {post.cover_image && <meta property="og:image" content={post.cover_image} />}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt,
          image: post.cover_image || undefined,
          datePublished: post.created_at,
          dateModified: post.updated_at,
          author: { "@type": "Organization", name: "Yes Global Consultants" },
          publisher: { "@type": "Organization", name: "Yes Global Consultants" },
        })}</script>
      </Helmet>
      <PageNav />

      <article className="mx-auto max-w-[860px] px-6 lg:px-0 pt-16 pb-24">
        <Link to="/blog" data-testid="blogpost-back" className="inline-flex items-center gap-2 font-body text-sm text-white/50 hover:text-[#00D166] transition-colors mb-10">
          <ArrowLeft size={16} /> Insights
        </Link>

        <div className="flex flex-wrap gap-2 mb-6">
          {(post.tags || []).map((t) => (
            <span key={t} className="font-body text-[10px] tracking-wide uppercase text-[#00D166] border border-[#00D166]/30 px-2 py-1">{t}</span>
          ))}
        </div>

        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.05]">{post.title}</h1>
        <div className="mt-6 flex items-center gap-4 font-body text-sm text-white/40">
          <span>{post.author}</span>
          <span className="h-1 w-1 rounded-full bg-white/30" />
          <span>{fmtDate(post.created_at)}</span>
        </div>

        {post.cover_image && (
          <div className="mt-10 aspect-[16/9] overflow-hidden border border-white/10">
            <img src={post.cover_image} alt={post.title} className="h-full w-full object-cover" />
          </div>
        )}

        <div className="mt-12" data-testid="blogpost-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{post.content}</ReactMarkdown>
        </div>

        <div className="mt-16 border-t border-white/10 pt-10">
          <div className="bg-[#050B14] border border-white/10 p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <p className="font-serifed italic text-2xl text-white">Building your leadership team?</p>
              <p className="mt-2 font-body text-sm text-white/50">Talk to our executive search practice.</p>
            </div>
            <Link to="/#contact" className="font-body text-sm font-semibold bg-[#00D166] text-[#030712] px-7 py-4 hover:bg-white transition-colors duration-300 whitespace-nowrap">Consult Us</Link>
          </div>
        </div>
      </article>
      <Footer />
    </div>
  );
}
